import { ImoovaImage } from "@/app/Media/Components/Image";
import { useWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignment";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import dayjs from "dayjs";
import {
  WorkOrderAssignmentRecordQuery,
  ExpenseType,
  ExpensePaidBy,
  Currency,
  UpdateWorkOrderAssignmentInput,
  ModelType,
  MediaCollection,
  PayoutStatus,
  TaxRegistration,
  WorkOrderAssignmentStatus,
} from "@/gql/graphql";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { ActivityLogFeed } from "@/app/ActivityLogs/Components/ActivityLogFeed";
import { AgreementsSection } from "@/app/ProDrivers/Agreements/Components/AgreementsSection";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { AddPayoutDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AddPayoutDialog";
import { AddDriverPayDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AddDriverPayDialog";
import { AddDriverExpenseDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AddDriverExpenseDialog";
import { AddImoovaExpenseDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AddImoovaExpenseDialog";
import { EditExpenseDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/EditExpenseDialog";
import { EditReceiptDialog } from "@/app/Refunds/Components/EditReceiptDialog";
import { AssignmentImagesDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AssignmentImagesDialog";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { WorkOrderAssignmentDetailsForm } from "@/app/ProDrivers/WorkOrders/Components/Forms/WorkOrderAssignmentDetailsForm";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderDriverMutations";
import { Button } from "@/components/catalyst/button";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from "@heroicons/react/20/solid";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useWorkOrderAssignmentPayoutActions } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignmentPayoutActions";
import { PhotoIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  deleteWorkOrderAssignmentExpense,
  uploadExpenseReceipts,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderExpenseMutations";
import {
  deleteReceiptMutation,
  reanalyzeReceiptMutation,
} from "@/app/Refunds/GraphQL/refundMutations";
import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { IMAGE_AND_PDF_ACCEPT } from "@/lib/FileUpload/accept";
import { toast } from "sonner";
import { getTaxRate } from "@/app/Payments/Utils/taxRates";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { cn } from "@/lib/utils";

type Assignment = NonNullable<
  WorkOrderAssignmentRecordQuery["workOrderAssignment"]
>;
type Payout = Assignment["payouts"][number];
type Expense = Assignment["expenses"][number];

export function AssignmentSheetContent({
  assignmentId,
}: {
  assignmentId: string;
}) {
  const { isAdmin } = useTenant();
  const assignment = useWorkOrderAssignment(assignmentId);

  const driverExpenseTotal = assignment.expenses
    .filter(
      (e) =>
        e.paid_by !== ExpensePaidBy.Imoova || e.type === ExpenseType.DriverPay,
    )
    .reduce((sum, e) => sum + e.payout_amount, 0);

  return (
    <div className="space-y-6">
      <AssignmentDetailsSection assignment={assignment} />
      {isAdmin ? (
        <>
          <AgreementsSection workOrderAssignmentId={assignment.id} />
          <ExpensesSection
            expenses={assignment.expenses}
            payouts={assignment.payouts}
            assignmentId={assignment.id}
            currency={assignment.workOrder.currency}
            taxRegistration={assignment.proDriver.tax_registration}
            assignmentStatus={assignment.status}
          />
          <PayoutsSection
            payouts={assignment.payouts}
            assignmentId={assignment.id}
            defaultAmount={driverExpenseTotal}
            currency={assignment.workOrder.currency}
          />
        </>
      ) : null}
      <ActivityLogFeed
        modelId={assignment.id}
        modelType={ModelType.WorkOrderAssignment}
      />
    </div>
  );
}

function AssignmentDetailsSection({ assignment }: { assignment: Assignment }) {
  const { mutateAsync } = useGqlMutation(updateWorkOrderAssignment);
  const { departureAddress, deliveryAddress } = assignment.workOrder;
  const { open: openImagesDialog } = useDialog(AssignmentImagesDialog);

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 pb-4">
      <RecordScreenFormCard<UpdateWorkOrderAssignmentInput>
        title="Assignment Details"
        initialValues={{
          pickup_date: assignment.pickup_date,
          pickup_time: assignment.pickup_time,
          delivery_date: assignment.delivery_date,
          delivery_time: assignment.delivery_time,
          booking_reference: assignment.booking_reference,
          driver_notes: assignment.driver_notes,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: assignment.id,
            input: values,
          });
        }}
        view={
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-8">
              <div className="relative border-l-4 border-blue-200 pl-6">
                <div className="absolute -left-[10px] -top-[3px] rounded-full bg-white p-1">
                  <div className="flex size-5 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                    A
                  </div>
                </div>
                <div className="font-semibold">{departureAddress.city}</div>
                <div className="mt-1 font-bold text-blue-600">
                  {assignment.pickup_date
                    ? dayjs(assignment.pickup_date).format("ddd, MMM D")
                    : "--"}
                  {assignment.pickup_time
                    ? ` • ${assignment.pickup_time}`
                    : null}
                </div>
                <address className="mt-3 grid gap-0.5 text-xs not-italic text-gray-500">
                  {departureAddress.formatted_address_line_1 ? (
                    <p>{departureAddress.formatted_address_line_1}</p>
                  ) : null}
                  {departureAddress.formatted_address_line_2 ? (
                    <p>{departureAddress.formatted_address_line_2}</p>
                  ) : null}
                </address>
                <button
                  type="button"
                  onClick={() =>
                    openImagesDialog({
                      title: "Departure Images",
                      assignmentId: assignment.id,
                      collection: MediaCollection.DepartureImage,
                    })
                  }
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  <PhotoIcon className="size-3.5" />
                  Photos
                  {assignment.departureImages.length > 0
                    ? ` (${assignment.departureImages.length})`
                    : null}
                </button>
              </div>

              <div className="relative border-l-4 border-green-200 pl-6">
                <div className="absolute -left-[10px] -top-[3px] rounded-full bg-white p-1">
                  <div className="flex size-5 items-center justify-center rounded-full bg-green-500 text-xs font-bold text-white">
                    B
                  </div>
                </div>
                <div className="font-semibold">{deliveryAddress.city}</div>
                <div className="mt-1 font-bold text-green-600">
                  {assignment.delivery_date
                    ? dayjs(assignment.delivery_date).format("ddd, MMM D")
                    : "--"}
                  {assignment.delivery_time
                    ? ` • ${assignment.delivery_time}`
                    : null}
                </div>
                <address className="mt-3 grid gap-0.5 text-xs not-italic text-gray-500">
                  {deliveryAddress.formatted_address_line_1 ? (
                    <p>{deliveryAddress.formatted_address_line_1}</p>
                  ) : null}
                  {deliveryAddress.formatted_address_line_2 ? (
                    <p>{deliveryAddress.formatted_address_line_2}</p>
                  ) : null}
                </address>
                <button
                  type="button"
                  onClick={() =>
                    openImagesDialog({
                      title: "Delivery Images",
                      assignmentId: assignment.id,
                      collection: MediaCollection.DeliveryImage,
                    })
                  }
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-800"
                >
                  <PhotoIcon className="size-3.5" />
                  Photos
                  {assignment.deliveryImages.length > 0
                    ? ` (${assignment.deliveryImages.length})`
                    : null}
                </button>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-4">
              {assignment.booking_reference ? (
                <div>
                  <dt className="text-gray-500">Booking Reference</dt>
                  <dd className="font-medium text-gray-900">
                    {assignment.booking_reference}
                  </dd>
                </div>
              ) : null}
              {assignment.driver_notes ? (
                <div className="col-span-2">
                  <dt className="text-gray-500">Driver Notes</dt>
                  <dd className="font-medium text-gray-900">
                    {assignment.driver_notes}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        }
      >
        <WorkOrderAssignmentDetailsForm autoFocusField="driver_notes" />
      </RecordScreenFormCard>
    </div>
  );
}

function PayoutsSection({
  payouts,
  assignmentId,
  defaultAmount,
  currency,
}: {
  payouts: Payout[];
  assignmentId: string;
  defaultAmount: number;
  currency: Currency;
}) {
  const { open: openPayoutDialog } = useDialog(AddPayoutDialog);
  const getActions = useWorkOrderAssignmentPayoutActions();

  const nonVoidedPayouts = payouts.filter(
    (p) => p.status !== PayoutStatus.Voided,
  );
  const total = nonVoidedPayouts.reduce(
    (sum, p) => sum + (p.converted_amount ?? p.amount),
    0,
  );
  const showTotal = nonVoidedPayouts.length > 1;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Payouts</h3>
        <Button
          plain
          onClick={() =>
            openPayoutDialog({
              workOrderAssignmentId: assignmentId,
            })
          }
        >
          <PlusIcon className="size-4" />
          Add
        </Button>
      </div>
      <div className="px-4 py-3">
        {payouts.length === 0 ? (
          <p className="text-sm text-gray-500">No payouts recorded</p>
        ) : (
          <div className="space-y-2">
            {payouts.map((payout) => {
              const isPending = payout.status === PayoutStatus.Pending;
              const isVoided = payout.status === PayoutStatus.Voided;
              const isCurrencyMismatch =
                payout.converted_currency != null &&
                payout.currency !== payout.converted_currency;

              return (
                <div
                  key={payout.id}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-4">
                    <span
                      className={cn(
                        "font-medium text-gray-900",
                        isVoided && "text-gray-400 line-through",
                      )}
                    >
                      {formatCurrency(payout.amount, payout.currency)}
                    </span>
                    {isCurrencyMismatch && payout.converted_amount != null ? (
                      <span
                        className={cn(
                          "text-xs text-gray-500",
                          isVoided && "line-through",
                        )}
                      >
                        ≈{" "}
                        {formatCurrency(
                          payout.converted_amount,
                          payout.converted_currency!,
                        )}{" "}
                        @ {payout.exchange_rate}
                      </span>
                    ) : null}
                    {isPending ? (
                      <span className="inline-flex items-center rounded-full border border-yellow-200 bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        Pending
                      </span>
                    ) : isVoided ? (
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        Voided
                      </span>
                    ) : (
                      <span className="text-gray-500">
                        {payout.paid_at ? formatDate(payout.paid_at) : "--"}
                      </span>
                    )}
                    {payout.transaction_reference ? (
                      <span className="text-gray-500">
                        {payout.transaction_reference}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex gap-1">
                    <IconMenuActions actions={getActions(payout)} />
                  </div>
                </div>
              );
            })}
            {showTotal ? (
              <div className="space-y-1 border-t border-gray-200 pt-2 text-sm">
                <div className="flex items-center justify-between font-semibold text-gray-900">
                  <span>Total</span>
                  <span
                    className={cn(total !== defaultAmount && "text-red-600")}
                  >
                    {formatCurrency(total, currency)}
                  </span>
                </div>
                {total !== defaultAmount ? (
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Due to driver</span>
                    <span>{formatCurrency(defaultAmount, currency)}</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function getExpenseTypeLabel(type: ExpenseType): string {
  switch (type) {
    case ExpenseType.DriverPay:
      return "Driver Pay";
    case ExpenseType.Fuel:
      return "Fuel";
    case ExpenseType.Ferry:
      return "Ferry";
    case ExpenseType.Flight:
      return "Flight";
    case ExpenseType.Accommodation:
      return "Accommodation";
    case ExpenseType.Transportation:
      return "Transportation";
    case ExpenseType.Other:
      return "Other";
    default:
      return type;
  }
}

function getExpenseTypeAccent(type: ExpenseType): string {
  switch (type) {
    case ExpenseType.DriverPay:
      return "border-l-blue-500";
    case ExpenseType.Fuel:
      return "border-l-amber-500";
    case ExpenseType.Ferry:
      return "border-l-cyan-500";
    case ExpenseType.Flight:
      return "border-l-violet-500";
    case ExpenseType.Accommodation:
      return "border-l-rose-500";
    case ExpenseType.Transportation:
      return "border-l-yellow-500";
    case ExpenseType.Other:
      return "border-l-gray-400";
    default:
      return "border-l-gray-400";
  }
}

export function ExpensesSection({
  expenses,
  payouts,
  assignmentId,
  taxRegistration,
  assignmentStatus,
}: {
  expenses: Expense[];
  payouts: Payout[];
  assignmentId: string;
  currency: Currency;
  taxRegistration: TaxRegistration;
  assignmentStatus: WorkOrderAssignmentStatus;
}) {
  const { open: openDriverPayDialog } = useDialog(AddDriverPayDialog);
  const { open: openDriverExpenseDialog } = useDialog(AddDriverExpenseDialog);
  const { open: openImoovaExpenseDialog } = useDialog(AddImoovaExpenseDialog);

  const isReceiptsLocked =
    assignmentStatus === WorkOrderAssignmentStatus.ReceiptsApproved;
  const hasDriverPay = expenses.some((e) => e.type === ExpenseType.DriverPay);

  const driverPayExpenses = expenses.filter(
    (e) => e.type === ExpenseType.DriverPay,
  );
  const driverExpenses = expenses.filter(
    (e) =>
      e.type !== ExpenseType.DriverPay && e.paid_by === ExpensePaidBy.Driver,
  );
  const imoovaExpenses = expenses.filter(
    (e) =>
      e.type !== ExpenseType.DriverPay && e.paid_by === ExpensePaidBy.Imoova,
  );

  const driverPayPayout = driverPayExpenses.reduce(
    (sum, e) => sum + e.payout_amount,
    0,
  );
  const driverBudget = driverExpenses.reduce((sum, e) => sum + e.amount, 0);
  const driverActual = driverExpenses.reduce(
    (sum, e) => sum + e.payout_amount,
    0,
  );
  const imoovaBudget = imoovaExpenses.reduce((sum, e) => sum + e.amount, 0);
  const imoovaActual = imoovaExpenses.reduce(
    (sum, e) => sum + e.payout_amount,
    0,
  );
  const driverPayBudget = driverPayExpenses.reduce(
    (sum, e) => sum + e.amount,
    0,
  );
  const totalBudget = driverPayBudget + driverBudget + imoovaBudget;
  const totalActual = driverPayPayout + driverActual + imoovaActual;
  const payoutsTotal = payouts
    .filter((p) => p.status !== PayoutStatus.Voided)
    .reduce((sum, p) => sum + (p.converted_amount ?? p.amount), 0);
  const dueToDriver = driverPayPayout + driverActual - payoutsTotal;
  const summCurrency = expenses[0]?.currency;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">Expenses</h3>
        {isReceiptsLocked ? null : (
          <div className="flex gap-1">
            <Button
              plain
              disabled={hasDriverPay}
              onClick={() =>
                openDriverPayDialog({ workOrderAssignmentId: assignmentId })
              }
            >
              <PlusIcon className="size-4" />
              Driver Pay
            </Button>
            <Button
              plain
              onClick={() =>
                openDriverExpenseDialog({ workOrderAssignmentId: assignmentId })
              }
            >
              <PlusIcon className="size-4" />
              Driver Expense
            </Button>
            <Button
              plain
              onClick={() =>
                openImoovaExpenseDialog({ workOrderAssignmentId: assignmentId })
              }
            >
              <PlusIcon className="size-4" />
              Imoova Expense
            </Button>
          </div>
        )}
      </div>
      {expenses.length === 0 ? (
        <div className="px-4 py-3">
          <p className="text-sm text-gray-500">No expenses recorded</p>
        </div>
      ) : (
        <div>
          {driverPayExpenses.length > 0 || driverExpenses.length > 0 ? (
            <div className="px-4 py-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                Driver Expenses
              </p>
              <div className="space-y-3">
                {[...driverPayExpenses, ...driverExpenses].map((expense) => (
                  <ExpenseItem
                    key={expense.id}
                    expense={expense}
                    taxRegistration={taxRegistration}
                    isReceiptsLocked={isReceiptsLocked}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {imoovaExpenses.length > 0 ? (
            <div className="border-t border-gray-200 px-4 py-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                Imoova Paid
              </p>
              <div className="space-y-3">
                {imoovaExpenses.map((expense) => (
                  <ExpenseItem
                    key={expense.id}
                    expense={expense}
                    taxRegistration={taxRegistration}
                    isReceiptsLocked={isReceiptsLocked}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {summCurrency ? (
            <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-xs font-medium uppercase tracking-wide text-gray-400">
                  <span />
                  <div className="flex gap-4">
                    <span className="w-24 text-right">Budget</span>
                    <span className="w-24 text-right">Actual</span>
                    <span className="w-16 text-right">Diff</span>
                  </div>
                </div>
                {driverPayExpenses.length > 0 ? (
                  <SummaryRow
                    label="Driver Pay"
                    budget={driverPayBudget}
                    actual={driverPayPayout}
                    currency={summCurrency}
                  />
                ) : null}
                {driverExpenses.length > 0 ? (
                  <SummaryRow
                    label="Driver Expenses"
                    budget={driverBudget}
                    actual={driverActual}
                    currency={summCurrency}
                  />
                ) : null}
                {imoovaExpenses.length > 0 ? (
                  <SummaryRow
                    label="Imoova Expenses"
                    budget={imoovaBudget}
                    actual={imoovaActual}
                    currency={summCurrency}
                  />
                ) : null}
                <div className="flex justify-between border-t border-gray-200 pt-2">
                  <span className="text-gray-500">Subtotal</span>
                  <div className="flex gap-4">
                    <span className="w-24 text-right text-gray-400">
                      {formatCurrency(totalBudget, summCurrency)}
                    </span>
                    <span className="w-24 text-right font-medium text-gray-900">
                      {formatCurrency(totalActual, summCurrency)}
                    </span>
                    <DiffBadge budget={totalBudget} actual={totalActual} />
                  </div>
                </div>
                {payoutsTotal > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Payouts</span>
                    <div className="flex gap-4">
                      <span className="w-24" />
                      <span className="w-24 text-right text-gray-900">
                        {formatCurrency(payoutsTotal, summCurrency)}
                      </span>
                      <span className="w-16" />
                    </div>
                  </div>
                ) : null}
                <div className="flex justify-between pt-1">
                  <span className="font-semibold text-gray-900">
                    Due to Driver
                  </span>
                  <div className="flex gap-4">
                    <span className="w-24" />
                    <span className="w-24 text-right font-semibold text-gray-900">
                      {formatCurrency(dueToDriver, summCurrency, {
                        showZero: true,
                      })}
                    </span>
                    <span className="w-16" />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function ExpenseItem({
  expense,
  taxRegistration,
  isReceiptsLocked,
}: {
  expense: Expense;
  taxRegistration: TaxRegistration;
  isReceiptsLocked: boolean;
}) {
  const { open: openEditDialog } = useDialog(EditExpenseDialog);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync: deleteExpenseMutation } = useGqlMutation(
    deleteWorkOrderAssignmentExpense,
  );
  const { mutateAsync: uploadReceiptsMutation } = useGqlMutation(
    uploadExpenseReceipts,
  );

  const hasReceipts = expense.receipts.length > 0;
  const isDriverPay = expense.type === ExpenseType.DriverPay;
  const typeLabel = getExpenseTypeLabel(expense.type);
  const accentColor = getExpenseTypeAccent(expense.type);
  const taxRate = getTaxRate(taxRegistration);
  const hasTax = isDriverPay && taxRate > 0;

  function handleEdit() {
    openEditDialog({ expense: { ...expense, taxRegistration } });
  }

  function handleDelete() {
    openConfirmDelete({
      title: `Delete ${typeLabel}`,
      message: `Are you sure you want to delete this ${typeLabel.toLowerCase()}?`,
      onDelete: async () => {
        await deleteExpenseMutation({ id: expense.id });
        toast.success("Expense deleted");
      },
    });
  }

  async function handleUploadReceipt(
    uploads: { file: File; filePath: string }[],
  ) {
    if (uploads.length === 0) return;
    try {
      await uploadReceiptsMutation({
        id: expense.id,
        filePaths: uploads.map((upload) => upload.filePath),
      });
      toast.success(
        uploads.length === 1
          ? "Receipt added successfully"
          : `${uploads.length} receipts added successfully`,
      );
    } catch {
      toast.error("Failed to add receipt");
    }
  }

  const receiptContent = !isDriverPay ? (
    <>
      {hasReceipts ? (
        <ReceiptGrid expense={expense} isReceiptsLocked={isReceiptsLocked} />
      ) : null}
      {isReceiptsLocked ? null : (
        <FileUploader
          onUpload={handleUploadReceipt}
          maxFileCount={10}
          className="!h-auto !min-h-0 !py-3"
          accept={IMAGE_AND_PDF_ACCEPT}
          fileTypeLabel="Supports JPEG, PNG, WebP, AVIF, and PDF formats"
        />
      )}
    </>
  ) : null;

  return (
    <div
      className={cn(
        "space-y-2 rounded-lg border border-l-4 border-gray-200 bg-white p-3",
        accentColor,
      )}
    >
      <div className="flex items-center justify-between text-sm">
        <div>
          <p className="font-semibold text-gray-900">{typeLabel}</p>
          {hasTax ? (
            <p className="mt-0.5 text-xs text-gray-500">
              Includes {taxRegistration.replace("_", " ")}
            </p>
          ) : null}
          {expense.description ? (
            <p className="mt-0.5 text-xs text-gray-500">
              {expense.description}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {!isDriverPay ? (
              <>
                <p className="text-xs text-gray-500">
                  Claimable:{" "}
                  {formatCurrency(
                    expense.receipts.reduce(
                      (sum, r) =>
                        sum + (r.claimable_converted_amount_inc_tax ?? 0),
                      0,
                    ),
                    expense.currency,
                  )}
                </p>
                {expense.receipts.some(
                  (r) => r.claimable_amount_inc_tax !== r.amount_inc_tax,
                ) ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <ExclamationTriangleIcon className="size-4 text-amber-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Receipt total:{" "}
                          {formatCurrency(
                            expense.receipts.reduce(
                              (sum, r) => sum + r.amount_inc_tax,
                              0,
                            ),
                            expense.currency,
                          )}
                        </p>
                        <p>
                          Claimable:{" "}
                          {formatCurrency(
                            expense.receipts.reduce(
                              (sum, r) =>
                                sum +
                                (r.claimable_converted_amount_inc_tax ?? 0),
                              0,
                            ),
                            expense.currency,
                          )}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </>
            ) : null}
            <p className="text-base font-semibold text-gray-900">
              {formatCurrency(expense.amount, expense.currency)}
            </p>
          </div>
          {isReceiptsLocked ? null : (
            <div className="flex gap-1">
              <Button plain onClick={handleEdit} title="Edit">
                <PencilIcon className="size-4" />
              </Button>
              <Button plain onClick={handleDelete} title="Delete">
                <TrashIcon className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
      {receiptContent}
    </div>
  );
}

function ReceiptGrid({
  expense,
  isReceiptsLocked,
}: {
  expense: Expense;
  isReceiptsLocked: boolean;
}) {
  const { open: openEditReceiptDialog } = useDialog(EditReceiptDialog);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync: deleteReceipt } = useGqlMutation(deleteReceiptMutation);
  const { mutateAsync: reanalyzeReceipt } = useGqlMutation(
    reanalyzeReceiptMutation,
  );

  function handleEdit(receiptId: string) {
    openEditReceiptDialog({ receiptId });
  }

  function handleDelete(receiptId: string) {
    openConfirmDelete({
      title: "Delete Receipt",
      message: "Are you sure you want to delete this receipt?",
      onDelete: async () => {
        await deleteReceipt({ id: receiptId });
        toast.success("Receipt deleted");
      },
    });
  }

  async function handleReanalyze(receiptId: string) {
    try {
      await reanalyzeReceipt({ id: receiptId });
      toast.success("Receipt re-analyzed");
    } catch {
      toast.error("Failed to re-analyze receipt");
    }
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {expense.receipts.map((receipt) => {
        const isCurrencyMismatch = receipt.currency !== expense.currency;

        return (
          <div
            key={receipt.id}
            className={cn(
              "group relative overflow-hidden rounded-lg border bg-white shadow-sm",
              isCurrencyMismatch
                ? "border-amber-300 ring-1 ring-amber-200"
                : "border-gray-100",
            )}
          >
            {isReceiptsLocked ? null : (
              <div className="absolute right-1 top-1 z-10 flex gap-0.5 rounded-md bg-white/90 opacity-0 shadow-sm backdrop-blur-sm transition-opacity group-hover:opacity-100">
                <Button
                  plain
                  onClick={() => handleReanalyze(receipt.id)}
                  title="Re-analyze Receipt"
                >
                  <ArrowPathIcon className="size-3" />
                </Button>
                <Button
                  plain
                  onClick={() => handleEdit(receipt.id)}
                  title="Edit Receipt"
                >
                  <PencilIcon className="size-3" />
                </Button>
                <Button
                  plain
                  onClick={() => handleDelete(receipt.id)}
                  title="Delete Receipt"
                >
                  <TrashIcon className="size-3" />
                </Button>
              </div>
            )}
            {receipt.image ? (
              receipt.image.mime_type === "application/pdf" ? (
                <a
                  href={receipt.image.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-28 w-full flex-col items-center justify-center gap-1 bg-gray-50 transition-colors hover:bg-gray-100"
                >
                  <DocumentTextIcon className="size-8 text-gray-400" />
                  <span className="text-xs font-medium text-blue-600">
                    View PDF
                  </span>
                </a>
              ) : isReceiptsLocked ? (
                <ImoovaImage
                  media={receipt.image}
                  alt="Receipt"
                  className="h-28 w-full object-cover"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => handleEdit(receipt.id)}
                  className="w-full cursor-pointer transition-opacity hover:opacity-80"
                >
                  <ImoovaImage
                    media={receipt.image}
                    alt="Receipt"
                    className="h-28 w-full object-cover"
                  />
                </button>
              )
            ) : null}
            <div className="p-2.5 text-xs">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-gray-700">{receipt.type}</p>
                  {receipt.date ? (
                    <p className="text-gray-500">{formatDate(receipt.date)}</p>
                  ) : null}
                </div>
                <div className="text-right">
                  {receipt.claimable_amount_inc_tax < receipt.amount_inc_tax ? (
                    <>
                      <p className="text-gray-400 line-through">
                        {formatCurrency(
                          receipt.amount_inc_tax,
                          receipt.currency,
                        )}
                      </p>
                      <p className="font-medium text-gray-900">
                        {formatCurrency(
                          receipt.claimable_amount_inc_tax,
                          receipt.currency,
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="font-medium text-gray-900">
                      {formatCurrency(receipt.amount_inc_tax, receipt.currency)}
                    </p>
                  )}
                  <p className="text-gray-500">
                    Tax {formatCurrency(receipt.amount_tax, receipt.currency)}
                  </p>
                </div>
              </div>
              {receipt.claimable_amount_inc_tax !== receipt.amount_inc_tax ? (
                <div className="mt-1.5 flex items-center gap-1 rounded bg-amber-50 px-1.5 py-1 text-amber-700">
                  <ExclamationTriangleIcon className="size-3.5 flex-shrink-0 text-amber-500" />
                  <p className="font-medium">
                    {Math.round(
                      (receipt.claimable_amount_inc_tax /
                        receipt.amount_inc_tax) *
                        100,
                    )}
                    % claimable
                  </p>
                </div>
              ) : null}
              {isCurrencyMismatch && receipt.converted_currency ? (
                <div className="mt-1.5 rounded bg-amber-50 px-1.5 py-1 text-amber-700">
                  <p className="font-medium">
                    {formatCurrency(
                      receipt.claimable_converted_amount_inc_tax,
                      receipt.converted_currency,
                    )}{" "}
                    <span className="font-normal text-amber-500">
                      @ {receipt.exchange_rate}
                    </span>
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DiffBadge({ budget, actual }: { budget: number; actual: number }) {
  if (budget === 0) {
    return <span className="w-16 text-right text-gray-400">—</span>;
  }

  const diff = ((actual - budget) / budget) * 100;
  const isOver = diff > 0;
  const isUnder = diff < 0;

  return (
    <span
      className={cn(
        "w-16 text-right text-xs font-medium",
        isOver ? "text-red-600" : isUnder ? "text-green-600" : "text-gray-400",
      )}
    >
      {isOver ? "+" : ""}
      {diff.toFixed(0)}%
    </span>
  );
}

function SummaryRow({
  label,
  budget,
  actual,
  currency,
}: {
  label: string;
  budget: number;
  actual: number;
  currency: Currency;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <div className="flex gap-4">
        <span className="w-24 text-right text-gray-400">
          {formatCurrency(budget, currency)}
        </span>
        <span className="w-24 text-right text-gray-900">
          {formatCurrency(actual, currency)}
        </span>
        <DiffBadge budget={budget} actual={actual} />
      </div>
    </div>
  );
}
